import { useState } from 'react';
import { Template } from '../data/Types';
import { Link } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, Eye, Zap, ArrowRight } from 'lucide-react';

const TemplateCard: React.FC<{ 
  template: Template, 
  likes?: number, 
  dislikes?: number, 
  onLike?: () => void, 
  onDislike?: () => void 
}> = ({ template,   onLike, onDislike }) => {
  const [userAction, setUserAction] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const openDemoPopup = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    window.open(template.demoUrl, '_blank', 'width=800,height=800');
  };

  const handleLike = () => {
    if (onLike && userAction !== 'like') {
      setUserAction('like');
      onLike();
    }
  };

  const handleDislike = () => {
    if (onDislike && userAction !== 'dislike') {
      setUserAction('dislike');
      onDislike();
    }
  };

  return (
    <div
      className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative border border-gray-100"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section with Gradient Overlay */}
      <Link to={`/template/${template._id}`} className="block relative">
        <div className="aspect-video overflow-hidden relative bg-gray-100">
          <img
            src={template.image}
            alt={template.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <span className="bg-navy-900 text-white px-3 py-1 rounded-full text-sm">
              {template.premiumLicense}
            </span>
          </div>
        </div>
      </Link>

      {/* Card Content */}
      <div className="p-6 space-y-4 relative bg-white">
        {/* Title & Price */}
        <div className="flex justify-between items-start">
          <h3 className="text-xl font-semibold text-navy-900">
            {template.title}
            <span className="block mt-1 text-sm text-gray-500 font-normal">
              {template.marketplace}
            </span>
          </h3>
          <div className="text-right">
            <span className="text-2xl font-bold text-navy-900">
              ${template.price}
            </span>
            <span className="block text-sm text-gray-500">
              one-time 
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 line-clamp-2 leading-relaxed text-sm">
          {template.description}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={openDemoPopup}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-navy-50 text-navy-900 rounded-xl
                      hover:bg-navy-100 transition-colors duration-300 group/demo"
          >
            <Eye className="w-5 h-5 text-navy-600 group-hover/demo:animate-pulse" />
            <span className="font-medium">Live Preview</span>
          </button>
          <button
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-navy-900 text-white rounded-xl
                      hover:bg-navy-800 transition-colors duration-300 group/buy"
          >
            <Zap className="w-5 h-5 text-amber-400 group-hover/buy:animate-ping" />
            <span className="font-medium"> License Ops</span>
          </button>
        </div>

        {/* Interactive Footer */}
        <div className="pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            {/* Rating Controls */}
            <div className="flex gap-3">
              <button
                onClick={handleLike}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                  userAction === "like"
                    ? "text-green-600 bg-green-50 border border-green-100"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <ThumbsUp className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {template.likes?.count}
                </span>
              </button>
              <button
                onClick={handleDislike}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all ${
                  userAction === "dislike"
                    ? "text-red-600 bg-red-50 border border-red-100"
                    : "text-gray-600 hover:bg-gray-50"
                }`}
              >
                <ThumbsDown className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {template.dislikes?.count}
                </span>
              </button>
            </div>

            {/* Details Link */}
            <Link
              to={`/template/${template._id}`}
              className="flex items-center gap-1.5 text-navy-900 hover:text-navy-700 transition-colors"
            >
              <span className="text-sm font-medium">View Details</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Hover Rating Badge */}
        {isHovered && (
          <div
            className="absolute -top-4 right-1 bg-amber-500 text-white px-2 py-1 rounded-full 
                        flex items-center shadow-lg animate-fade-in"
          >
            <span className="text-sm font-medium">
              +{template.likes?.count - template.dislikes?.count}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateCard;